import { AG_GRID_LOCALE_DE, AG_GRID_LOCALE_EN, AG_GRID_LOCALE_IL } from '@ag-grid-community/locale';
import {
  AllCommunityModule,
  CellClickedEvent,
  type ColDef,
  GetRowIdFunc,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  IServerSideDatasource,
  LocaleModule,
  ModuleRegistry,
  RowModelType,
  RowSelectionOptions,
  SelectionChangedEvent,
  SelectionColumnDef,
  SideBarDef,
  themeQuartz,
} from 'ag-grid-community';
import {
  CellSelectionModule,
  ColumnsToolPanelModule,
  ContextMenuModule,
  ExcelExportModule,
  NumberFilterModule,
  RichSelectModule,
  ServerSideRowModelModule,
  SetFilterModule,
  SideBarModule,
} from 'ag-grid-enterprise';
import { Component, DestroyRef, computed, inject, input, output } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { AppTranslateService } from '../../../../core/services/app.translate.service';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { HeaderSearchFilterOptions } from '../../../../core/types/header-search';
import { NgTemplateOutlet } from '@angular/common';
import { RemoveInnerFilterEvent } from '../../../../core/types/ag-grid-table-filter';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// Register features
ModuleRegistry.registerModules([
  AllCommunityModule,
  LocaleModule,
  CellSelectionModule,
  RichSelectModule,
  SideBarModule,
  ColumnsToolPanelModule,
  SetFilterModule,
  NumberFilterModule,
  ContextMenuModule,
  ServerSideRowModelModule,
  ExcelExportModule,
]);

// Any other supported languages should also be added here
type locals = 'he' | 'en' | 'de';
const agGridLocale: Record<locals, Record<string, string> | undefined> = {
  he: {
    ...AG_GRID_LOCALE_IL,
  },
  en: {
    ...AG_GRID_LOCALE_EN,
  },
  de: {
    ...AG_GRID_LOCALE_DE,
  },
};

@Component({
  selector: 'app-ag-grid-table',
  imports: [AgGridAngular, NgTemplateOutlet],
  templateUrl: './ag-grid-table.component.html',
  styleUrl: './ag-grid-table.component.scss',
})
export class AgGridTableComponent {
  private destroyRef = inject(DestroyRef);
  private appTranslate = inject(AppTranslateService);

  gridApi!: GridApi;

  // This is mandatory configs for the table
  readonly colDefs = input.required<ColDef[]>();
  readonly dataSource = input<IServerSideDatasource>();

  // This is config for language and RTL/LTR support
  readonly enableRtl = input<boolean>(false);
  readonly language = input<locals>('en');
  protected localeText = computed(() => agGridLocale[this.language()]);

  readonly tooltipShowMode = input<'standard' | 'whenTruncated'>('whenTruncated');

  // This is for inner filter
  readonly removeInnerFilterEvent = input<BehaviorSubject<RemoveInnerFilterEvent | { removeAll: boolean }>>();
  readonly updateOneFilterProperty = input<BehaviorSubject<{ columnName: string; value: string } | null>>();
  readonly clearRowStateEvent = input<BehaviorSubject<boolean>>();

  // this is configs for columns tool panel
  sideBar = input<SideBarDef | string | string[] | boolean | null>({
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
        toolPanelParams: {
          suppressRowGroups: true,
          suppressValues: true,
          suppressPivots: true,
          suppressPivotMode: true,
        },
      },
    ],
    defaultToolPanel: '',
  });

  // This parameter shows row selection checkboxes
  readonly rowSelection = input<RowSelectionOptions | 'single' | 'multiple'>({
    mode: 'multiRow',
    headerCheckbox: true,
    enableSelectionWithoutKeys: true,
    enableClickSelection: false,
  });
  rowSelectedEvent = output<SelectionChangedEvent>();
  visibleColumnsEvent = output<string[]>();
  cellClickedEvent = output<CellClickedEvent>();

  // this is config for serverSide row model
  rowModelType: RowModelType = 'serverSide';
  getRowId: GetRowIdFunc = (params: GetRowIdParams) => {
    return params.data?.id;
  };

  // This column config that is applicable for each column by default, can be rewritten in colDefs
  readonly defaultColDef: ColDef = {
    sortable: true,
    editable: false,
    filter: true,
    filterParams: {
      buttons: ['apply', 'reset'],
      closeOnApply: true,
    },
    suppressHeaderMenuButton: true,
  };

  // This column config that is applicable for selection column only
  readonly selectionColumnDef: SelectionColumnDef = {
    width: 30,
    suppressMovable: true,
    pinned: this.appTranslate.isRTL() ? 'right' : 'left',
    lockPosition: this.appTranslate.isRTL() ? 'right' : 'left',
  };

  // this is for any custom icons
  readonly icons: Record<string, ((...args: unknown[]) => unknown) | string> = {
    selectOpen: '<img src="assets/icons/arrow-down.svg" alt="arrow"/>',
    richSelectOpen: '<img src="assets/icons/arrow-down.svg" alt="arrow"/>',
    filter: '<img src="assets/icons/icon_filter.svg" alt="filter"/>',
    columns: '<img src="assets/icons/settings.svg" alt="settings"/>',
    sortDescending: '<img src="assets/icons/sort_arrow_down.svg" alt="arrow"/>',
    sortAscending: '<img src="assets/icons/sort_arrow_up.svg" alt="arrow"/>',
  };

  readonly theme = themeQuartz.withParams({
    accentColor: '#0A3455',
    backgroundColor: '#FFFFFF',
    borderColor: '#C3CED9',
    browserColorScheme: 'inherit',
    cellTextColor: '#0A3455',
    chromeBackgroundColor: {
      ref: 'foregroundColor',
      mix: 0.07,
      onto: 'backgroundColor',
    },
    fontFamily: {
      googleFont: 'Rubik',
    },
    fontSize: 16,
    foregroundColor: '#0A3455',
    headerBackgroundColor: '#F5F9FF',
    headerFontFamily: {
      googleFont: 'Rubik',
    },
    headerFontSize: 16,
    headerRowBorder: true,
    headerTextColor: '#0A3455',
    rowVerticalPaddingScale: 1,
    rowHoverColor: '#F5F9FF',
    selectedRowBackgroundColor: '#F5F9FF',
    rowHeight: 64,
    headerHeight: 52,
    cellEditingBorder: false,
    menuShadow: { radius: 12, spread: 4, color: 'rgba(0, 0, 0, 0.16)' },
    menuBorder: {
      style: 'none',
    },
    menuBackgroundColor: 'white',
    rangeSelectionBorderStyle: 'none',
    buttonBorderRadius: 20,

    sideBarBackgroundColor: 'white',
    sideButtonBarBackgroundColor: '#F5F9FF',
    sideButtonSelectedBackgroundColor: '#F5F9FF',
    sideButtonSelectedBorder: false,
    sideButtonVerticalPadding: 10,
  });

  protected onRowSelectionChanged(event: SelectionChangedEvent) {
    this.rowSelectedEvent.emit(event);
  }

  clearRowState() {
    this.gridApi.deselectAll();
  }

  onColumnVisible() {
    const state = this.gridApi.getColumnState();
    const visibleCols = state.filter((s) => !s.hide).map((s) => s.colId);
    this.visibleColumnsEvent.emit(visibleCols);
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.onColumnVisible();
    this.subscribeToRemoveFilters();
    this.updateOneFilterPropertySubscription();
    this.clearRowStateSubscription();
  }

  private clearRowStateSubscription(): void {
    this.clearRowStateEvent()
      ?.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.clearRowState());
  }

  private updateOneFilterPropertySubscription(): void {
    this.updateOneFilterProperty()
      ?.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result) return;
        const model = this.gridApi.getFilterModel();
        if (result.value === HeaderSearchFilterOptions.ALL) {
          delete model[result.columnName];
        } else {
          model[result.columnName] = {
            filterType: 'set',
            values: [result.value],
          };
        }

        this.gridApi.setFilterModel(model);
        this.gridApi.onFilterChanged();
      });
  }

  private subscribeToRemoveFilters(): void {
    this.removeInnerFilterEvent()
      ?.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if ((result as { removeAll: boolean }).removeAll) {
          this.gridApi.setFilterModel({});
          this.gridApi.onFilterChanged();
        } else {
          const model = this.gridApi.getFilterModel();
          const res = result as RemoveInnerFilterEvent;

          const filter = model[res.columnName];
          if (!filter) return;

          if (Array.isArray(filter.values)) {
            const newValues = filter.values.filter((v: string) => v !== res.filterValue);

            if (newValues.length > 0) {
              model[res.columnName] = { ...filter, values: newValues };
            } else {
              delete model[res.columnName];
            }
          } else if (filter.filter !== undefined) {
            if (filter.filter === res.filterValue) {
              delete model[res.columnName];
            }
          } else if (filter.filterType === 'date') {
            // date filter (inRange / equals / etc.)
            delete model[res.columnName];
          }

          this.gridApi.setFilterModel(model);
          this.gridApi.onFilterChanged();
        }
      });
  }

  onFirstDataRendered() {
    this.gridApi.resetRowHeights();
  }

  protected onCellClicked(event: CellClickedEvent): void {
    this.cellClickedEvent.emit(event);
  }
}
